import React from 'react';
import {Badge, Tag, Typography} from 'antd';
import {AgentWorkflowStep} from '../../hooks/useAgentWebSocket';

const {Text} = Typography;
const CONNECTOR_COLOR = 'rgba(240, 180, 41, 0.35)';
const NODE_BACKGROUND = 'rgba(255, 255, 255, 0.03)';
const NODE_MIN_HEIGHT = 56;
const CONNECTOR_HORIZONTAL_GAP = 20;
const CONNECTOR_VERTICAL_GAP = 28;
const NEXT_ROW_CENTER_OFFSET = NODE_MIN_HEIGHT + CONNECTOR_VERTICAL_GAP + NODE_MIN_HEIGHT / 2;
const ARROW_SIZE = 6;
const TURN_DOT_SIZE = 8;
const ARROW_WIDTH = 10;
const ARROW_HEIGHT = 10;

export type FlowNodeStatus = 'finish' | 'process' | 'error' | 'blocked';

export interface FlowNodeView {
    nodeCode: string;
    nodeName: string;
    orderNo: number;
    status: FlowNodeStatus;
    message?: string;
    step?: AgentWorkflowStep;
}

const getSnakeColumns = () => 5;

const getNodeStatusColor = (status: FlowNodeStatus): string => {
    if (status === 'error') return '#ff7875';
    if (status === 'process') return 'var(--pt-primary)';
    if (status === 'blocked') return 'var(--pt-amber)';
    return 'var(--pt-bull)';
};

const getArrowStyle = (
    direction: 'left' | 'right',
    anchor: 'outer' | 'inner',
    top: string
): React.CSSProperties => {
    const horizontalStyle = anchor === 'outer'
        ? (direction === 'right'
            ? {right: `-${CONNECTOR_HORIZONTAL_GAP}px`}
            : {left: `-${CONNECTOR_HORIZONTAL_GAP}px`})
        : (direction === 'right'
            ? {right: '100%'}
            : {left: '100%'});
    return {
        position: 'absolute',
        top,
        transform: 'translateY(-50%)',
        width: `${ARROW_WIDTH}px`,
        height: `${ARROW_HEIGHT}px`,
        background: CONNECTOR_COLOR,
        clipPath: direction === 'right'
            ? 'polygon(0 0, 100% 50%, 0 100%)'
            : 'polygon(100% 0, 0 50%, 100% 100%)',
        zIndex: 0,
        ...horizontalStyle
    };
};

const getTurnDotStyle = (side: 'left' | 'right', top: string): React.CSSProperties => ({
    position: 'absolute',
    top,
    width: TURN_DOT_SIZE,
    height: TURN_DOT_SIZE,
    borderRadius: '50%',
    background: CONNECTOR_COLOR,
    boxShadow: '0 0 0 2px rgba(240, 180, 41, 0.08)',
    zIndex: 0,
    ...(side === 'right'
        ? {right: `-${CONNECTOR_HORIZONTAL_GAP + TURN_DOT_SIZE / 2}px`, transform: 'translateY(-50%)'}
        : {left: `-${CONNECTOR_HORIZONTAL_GAP + TURN_DOT_SIZE / 2}px`, transform: 'translateY(-50%)'})
});

interface AgentDebugFlowViewProps {
    steps: AgentWorkflowStep[];
    loading?: boolean;
    traceId?: string;
    completed?: boolean;
    failed?: boolean;
    onNodeClick?: (step: AgentWorkflowStep, index: number) => void;
}

const buildWsFlowNodes = (steps: AgentWorkflowStep[], completed?: boolean, failed?: boolean): FlowNodeView[] => {
    const nameMap: Record<string, string> = {
        'get_kline': 'K线数据',
        'get_position': '持仓',
        'get_balance': '账户扫描',
        'trade': '执行交易',
        'cancel_order': '撤销订单',
        'set_stop_order': '设置止损止盈',
        'get_candle_patterns': 'K线形态识别',
        'get_long_short_ratio': '检索多空比',
        'get_zone_patterns': '价格区域分析',
        'get_radar': '市场雷达',
        'get_topN': '合约排行榜',
        'get_user_exchange': '交易所查询',
        'get_user_trade_config': '交易配置查询',
        'submit_final_output': '决策',
        'AGENT_EXECUTING': 'Agent执行中',
        'DISPATCHED': '调度完成',
        'unknown': '模型响应解析中',
        '__agent_failed__': '执行失败',
    };
    return steps.map((step, index) => {
        const funcName = step.functionName || '';
        const stepType = step.stepType || step.type || '';
        let status: FlowNodeStatus;
        if (funcName === '__agent_failed__') {
            status = 'error';
        } else if (stepType === 'tool_call_debug_blocked') {
            status = 'blocked';
        } else if (index === steps.length - 1) {
            status = completed ? (failed ? 'error' : 'finish') : 'process';
        } else {
            status = 'finish';
        }
        let nodeName = nameMap[funcName] || (step.step ? step.step : (funcName || '处理中'));
        if (stepType === 'tool_call_debug_blocked') {
            nodeName = '调试拦截';
        }
        return {
            nodeCode: funcName || step.step || 'step',
            nodeName,
            orderNo: index + 1,
            status,
            message: step.summary || (step.result ? step.result.substring(0, 50) : undefined),
            step
        };
    });
};

const AgentDebugFlowView: React.FC<AgentDebugFlowViewProps> = ({
                                                                   steps,
                                                                   loading,
                                                                   traceId,
                                                                   completed,
                                                                   failed,
                                                                   onNodeClick
                                                               }) => {
    if (loading) {
        return (
            <div style={{padding: 16, textAlign: 'center'}}>
                <Badge status="processing"/>
                <Text style={{color: 'rgba(255,255,255,0.65)', marginLeft: 8}}>等待执行...</Text>
            </div>
        );
    }

    if (!steps || steps.length === 0) {
        if (!traceId) {
            return (
                <div style={{padding: 16, textAlign: 'center'}}>
                    <Text style={{color: 'rgba(255,255,255,0.45)', fontSize: 12}}>未触发执行，暂无流程数据</Text>
                </div>
            );
        }
        if (completed) {
            return (
                <div style={{padding: 16, textAlign: 'center'}}>
                    <Badge status={failed ? 'error' : 'success'}/>
                    <Text style={{color: 'rgba(255,255,255,0.65)', marginLeft: 8}}>
                        {failed ? '执行失败，无流程数据' : '执行完成，无流程步骤记录'}
                    </Text>
                </div>
            );
        }
        return (
            <div style={{padding: 16, textAlign: 'center'}}>
                <Badge status="processing"/>
                <Text style={{color: 'rgba(255,255,255,0.65)', marginLeft: 8}}>智能体执行中...</Text>
            </div>
        );
    }

    const nodes = buildWsFlowNodes(steps, completed, failed);
    const columns = getSnakeColumns();

    const renderSnakeFlow = () => {
        return (
            <div
                style={{
                    display: 'grid',
                    gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
                    columnGap: `${CONNECTOR_HORIZONTAL_GAP}px`,
                    rowGap: `${CONNECTOR_VERTICAL_GAP}px`,
                    width: '100%',
                    overflow: 'visible'
                }}
            >
                {nodes.map((node, index) => {
                    const row = Math.floor(index / columns);
                    const indexInRow = index % columns;
                    const rowItemCount = Math.min(columns, nodes.length - row * columns);
                    const isLeftToRightRow = row % 2 === 0;
                    const visualColumn = isLeftToRightRow ? indexInRow : columns - indexInRow - 1;
                    const isLastNode = index === nodes.length - 1;
                    const wrapsToNextRow = !isLastNode && indexInRow === rowItemCount - 1;
                    const hasNextInSameRow = !isLastNode && !wrapsToNextRow;
                    const connectorSide = isLeftToRightRow ? 'right' : 'left';
                    const arrowPointSide = isLeftToRightRow ? 'right' : 'left';
                    const nextRowArrowSide = isLeftToRightRow ? 'left' : 'right';
                    const statusColor = getNodeStatusColor(node.status);
                    const isProcessingNode = node.status === 'process';

                    return (
                        <div
                            key={`${node.nodeCode}-${node.orderNo}-${index}`}
                            style={{
                                gridColumn: `${visualColumn + 1} / span 1`,
                                gridRow: `${row + 1} / span 1`,
                                minWidth: 0,
                                position: 'relative',
                                overflow: 'visible',
                                cursor: onNodeClick ? 'pointer' : 'default'
                            }}
                            onClick={() => onNodeClick && node.step && onNodeClick(node.step, index)}
                        >
                            {hasNextInSameRow && (
                                <>
                                    <div style={{
                                        position: 'absolute', top: '50%', transform: 'translateY(-50%)',
                                        [connectorSide]: `-${CONNECTOR_HORIZONTAL_GAP}px`,
                                        width: `${CONNECTOR_HORIZONTAL_GAP}px`, height: '3px',
                                        background: CONNECTOR_COLOR, borderRadius: '999px', zIndex: 0
                                    }}/>
                                    <div style={getArrowStyle(arrowPointSide, 'outer', '50%')}/>
                                </>
                            )}
                            {wrapsToNextRow && (
                                <>
                                    <div style={{
                                        position: 'absolute', top: '50%', transform: 'translateY(-50%)',
                                        [connectorSide]: `-${CONNECTOR_HORIZONTAL_GAP}px`,
                                        width: `${CONNECTOR_HORIZONTAL_GAP}px`, height: '3px',
                                        background: CONNECTOR_COLOR, borderRadius: '999px', zIndex: 0
                                    }}/>
                                    <div style={{
                                        position: 'absolute', top: '50%', transform: 'translateY(-1px)',
                                        [connectorSide]: `-${CONNECTOR_HORIZONTAL_GAP}px`,
                                        width: '3px', height: `${NEXT_ROW_CENTER_OFFSET - NODE_MIN_HEIGHT / 2}px`,
                                        background: CONNECTOR_COLOR, borderRadius: '999px', zIndex: 0
                                    }}/>
                                    <div style={getTurnDotStyle(connectorSide, '50%')}/>
                                    <div style={{
                                        position: 'absolute', top: `${NEXT_ROW_CENTER_OFFSET}px`,
                                        transform: 'translateY(-50%)',
                                        [connectorSide]: `-${CONNECTOR_HORIZONTAL_GAP}px`,
                                        width: `${CONNECTOR_HORIZONTAL_GAP}px`, height: '3px',
                                        background: CONNECTOR_COLOR, borderRadius: '999px', zIndex: 0
                                    }}/>
                                    <div style={getTurnDotStyle(connectorSide, `${NEXT_ROW_CENTER_OFFSET}px`)}/>
                                    <div
                                        style={getArrowStyle(nextRowArrowSide, 'inner', `${NEXT_ROW_CENTER_OFFSET}px`)}/>
                                </>
                            )}
                            <div
                                title={node.message || node.nodeName}
                                style={{
                                    position: 'relative', zIndex: 1, height: `${NODE_MIN_HEIGHT}px`,
                                    padding: '8px 12px', borderRadius: '12px',
                                    background: isProcessingNode
                                        ? 'linear-gradient(90deg, rgba(240, 180, 41, 0.12), rgba(255, 255, 255, 0.04))'
                                        : NODE_BACKGROUND,
                                    border: `1px solid ${
                                        node.status === 'blocked' ? 'rgba(250, 173, 20, 0.55)'
                                            : isProcessingNode ? 'rgba(240, 180, 41, 0.55)'
                                                : 'rgba(255,255,255,0.12)'}`,
                                    boxShadow: isProcessingNode
                                        ? '0 0 0 1px rgba(240, 180, 41, 0.16) inset, 0 0 18px rgba(240, 180, 41, 0.12)'
                                        : 'none',
                                    display: 'flex', alignItems: 'center', gap: 10,
                                    animation: isProcessingNode ? 'flow-node-pulse 1.8s ease-in-out infinite' : undefined
                                }}
                            >
                                <div style={{
                                    width: 22, height: 22, borderRadius: '50%', display: 'flex',
                                    alignItems: 'center', justifyContent: 'center', background: statusColor,
                                    color: '#111', fontSize: 11, fontWeight: 700, flex: '0 0 auto',
                                    boxShadow: isProcessingNode ? '0 0 0 0 rgba(240, 180, 41, 0.45)' : 'none',
                                    animation: isProcessingNode ? 'flow-node-dot-pulse 1.8s ease-in-out infinite' : undefined
                                }}>
                                    {node.orderNo}
                                </div>
                                <div style={{minWidth: 0, flex: 1}}>
                                    <Text style={{
                                        fontSize: 12,
                                        color: 'rgba(255, 255, 255, 0.92)',
                                        display: 'block',
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis'
                                    }}>
                                        {node.nodeName}
                                    </Text>
                                </div>
                                <Text style={{
                                    fontSize: 11, color: statusColor, flex: '0 0 auto', whiteSpace: 'nowrap'
                                }}>
                                    {node.status === 'process' ? '处理中'
                                        : node.status === 'error' ? '失败'
                                            : node.status === 'blocked' ? '已拦截'
                                                : '完成'}
                                </Text>
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };

    return (
        <>
            <style>
                {`
                  @keyframes flow-node-pulse {
                    0%, 100% { transform: translateY(0); box-shadow: 0 0 0 1px rgba(240, 180, 41, 0.16) inset, 0 0 16px rgba(240, 180, 41, 0.10); }
                    50% { transform: translateY(-1px); box-shadow: 0 0 0 1px rgba(240, 180, 41, 0.26) inset, 0 0 26px rgba(240, 180, 41, 0.22); }
                  }
                  @keyframes flow-node-dot-pulse {
                    0%, 100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(240, 180, 41, 0.32); }
                    60% { transform: scale(1.08); box-shadow: 0 0 0 8px rgba(240, 180, 41, 0); }
                  }
                `}
            </style>
            <div style={{
                padding: '8px 0',
                border: '1px solid rgba(240, 180, 41, 0.2)',
                borderRadius: 8,
                background: 'rgba(240, 180, 41, 0.03)',
                marginTop: 8
            }}>
                {traceId && (
                    <div style={{padding: '0 12px 8px', display: 'flex', gap: 8, alignItems: 'center'}}>
                        <Tag color="gold">Debug</Tag>
                        <Text style={{color: 'rgba(255, 255, 255, 0.45)', fontFamily: 'monospace', fontSize: 11}}>
                            {traceId}
                        </Text>
                    </div>
                )}
                <div style={{padding: '0 12px'}}>
                    {renderSnakeFlow()}
                </div>
            </div>
        </>
    );
};

export default AgentDebugFlowView;
